import { FileDown, CheckCircle2, Circle, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SignActions } from "@/components/SignActions";
import { SigningRequestBadges } from "@/components/SigningRequestBadges";
import { nb, nok, formatDate, formatDateTime } from "@/lib/format";
import { SETTLEMENT_STATUS } from "./constants";

export function SettlementsTab({ settlements, onChanged }) {
  const cancel = async (id) => {
    try {
      await api.post(`/settlements/${id}/cancel`);
      toast.success("Sluttseddel kansellert");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (settlements.length === 0) {
    return <p className="p-8 text-center text-sm text-muted-foreground">Ingen sluttsedler ennå. Trykk «Generer sluttseddel» for å overføre aksjer.</p>;
  }

  return (
    <div className="divide-y divide-border/60">
      {settlements.map((st) => {
        const meta = SETTLEMENT_STATUS[st.status] || SETTLEMENT_STATUS.kansellert;
        return (
          <div key={st.id} data-testid={`settlement-${st.id}`} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium">{st.seller.name} <span className="text-muted-foreground">→</span> {st.buyer.name}</p>
                <p className="text-xs text-muted-foreground">{nb(st.shares)} {st.share_class} · {nok(st.price_per_share)}/aksje · {formatDate(st.date)}</p>
              </div>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.cls}`}>{meta.label}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs">
              {["seller", "buyer"].map((role) => (
                <span key={role} className="flex items-center gap-1.5">
                  {st[role].signed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-muted-foreground/40" />}
                  {role === "seller" ? "Selger" : "Kjøper"}: {st[role].signed ? `signert ${formatDateTime(st[role].signed_at)}${st[role].method === "bankid" ? " (BankID)" : ""}` : "avventer signering"}
                </span>
              ))}
            </div>
            {st.status === "venter_signering" ? <SigningRequestBadges entityType="settlement" entityId={st.id} /> : null}
            <div className="mt-3 flex flex-wrap gap-2">
              {st.status === "venter_signering" ? (
                <SignActions entityType="settlement" entityId={st.id} onChanged={onChanged} />
              ) : null}
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openSigned(`/settlements/${st.id}/pdf-link`).catch(() => toast.error("Kunne ikke åpne sluttseddelen"))} data-testid={`settlement-pdf-${st.id}`}>
                <FileDown className="h-4 w-4" /> Sluttseddel (PDF)
              </Button>
              {st.bankid_signed_path ? (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openSigned(`/signing/settlement/${st.id}/signed-link`).catch(() => toast.error("Kunne ikke åpne det signerte dokumentet"))} data-testid={`settlement-bankid-pdf-${st.id}`}>
                  <ShieldCheck className="h-4 w-4 text-emerald-600" /> BankID-signert PDF
                </Button>
              ) : null}
              {st.status === "venter_signering" ? (
                <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => cancel(st.id)} data-testid={`cancel-settlement-${st.id}`}>
                  <X className="h-4 w-4" /> Kanseller
                </Button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
